import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  IconButton, Box, Typography, FormControlLabel, Checkbox,
  Alert, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { executeQuery } from '../../api/connections';

const COLUMN_TYPES = [
  'TEXT',
  'VARCHAR(255)',
  'INTEGER',
  'BIGINT',
  'SERIAL',
  'BIGSERIAL',
  'BOOLEAN',
  'NUMERIC',
  'NUMERIC(10,2)',
  'REAL',
  'DOUBLE PRECISION',
  'DATE',
  'TIMESTAMP',
  'TIMESTAMPTZ',
  'JSON',
  'JSONB',
  'UUID',
];

const emptyColumn = () => ({
  name: '',
  type: 'TEXT',
  nullable: true,
  primaryKey: false,
  defaultValue: '',
});

const CreateTableDialog = ({ open, onClose, connectionId, schemas, onCreated }) => {
  const [tableName, setTableName] = useState('');
  const [schema, setSchema] = useState('public');
  const [columns, setColumns] = useState([{ ...emptyColumn(), name: 'id', type: 'SERIAL', nullable: false, primaryKey: true }]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleAddColumn = () => {
    setColumns([...columns, emptyColumn()]);
  };

  const handleRemoveColumn = (index) => {
    setColumns(columns.filter((_, i) => i !== index));
  };

  const handleColumnChange = (index, field, value) => {
    setColumns(columns.map((col, i) => i === index ? { ...col, [field]: value } : col));
  };

  const buildSQL = () => {
    const qualified = schema ? `"${schema}"."${tableName}"` : `"${tableName}"`;
    const pks = columns.filter((c) => c.primaryKey).map((c) => `"${c.name}"`);
    const colDefs = columns.map((col) => {
      let def = `  "${col.name}" ${col.type}`;
      if (!col.nullable) def += ' NOT NULL';
      if (col.defaultValue) def += ` DEFAULT ${col.defaultValue}`;
      return def;
    });
    if (pks.length > 0) {
      colDefs.push(`  PRIMARY KEY (${pks.join(', ')})`);
    }
    return `CREATE TABLE ${qualified} (\n${colDefs.join(',\n')}\n);`;
  };

  const handleCreate = async () => {
    if (!tableName.trim()) {
      setError('Table name is required');
      return;
    }
    if (columns.length === 0) {
      setError('At least one column is required');
      return;
    }
    if (columns.some((c) => !c.name.trim())) {
      setError('All columns must have a name');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const sql = buildSQL();
      await executeQuery(connectionId, sql);
      onCreated?.();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to create table');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setTableName('');
    setSchema('public');
    setColumns([{ ...emptyColumn(), name: 'id', type: 'SERIAL', nullable: false, primaryKey: true }]);
    setError('');
    setCreating(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Table</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Schema</InputLabel>
            <Select value={schema} label="Schema" onChange={(e) => setSchema(e.target.value)}>
              {(schemas || ['public']).map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Table Name"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            size="small"
            fullWidth
            autoFocus
          />
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Columns</Typography>

        {columns.map((col, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField
              label="Name"
              value={col.name}
              onChange={(e) => handleColumnChange(i, 'name', e.target.value)}
              size="small"
              sx={{ flex: 2 }}
            />
            <FormControl size="small" sx={{ flex: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select value={col.type} label="Type" onChange={(e) => handleColumnChange(i, 'type', e.target.value)}>
                {COLUMN_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Default"
              value={col.defaultValue}
              onChange={(e) => handleColumnChange(i, 'defaultValue', e.target.value)}
              size="small"
              sx={{ flex: 1.5 }}
            />
            <FormControlLabel
              control={<Checkbox checked={col.primaryKey} onChange={(e) => handleColumnChange(i, 'primaryKey', e.target.checked)} size="small" />}
              label="PK"
              sx={{ mx: 0 }}
            />
            <FormControlLabel
              control={<Checkbox checked={col.nullable} onChange={(e) => handleColumnChange(i, 'nullable', e.target.checked)} size="small" />}
              label="Null"
              sx={{ mx: 0 }}
            />
            <IconButton size="small" onClick={() => handleRemoveColumn(i)} disabled={columns.length <= 1}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Button size="small" startIcon={<AddIcon />} onClick={handleAddColumn} sx={{ mt: 1 }}>
          Add Column
        </Button>

        {tableName && columns.length > 0 && columns[0].name && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">Generated SQL</Typography>
            <pre style={{ margin: '4px 0 0', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{buildSQL()}</pre>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={creating || !tableName.trim() || columns.length === 0}
          startIcon={creating ? <CircularProgress size={16} /> : null}
        >
          Create Table
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTableDialog;
